import React, {useEffect, useState} from 'react';
import {Button, TextField} from '@mui/material';
import {gql} from '@apollo/client';
import {useMutation} from '@apollo/client/react';
import {toast} from 'react-toastify';

import CustomDialog from 'components/forms/CustomDialog';
import {DialogContextProvider, useDialogContext} from 'components/dialog/DialogContext';
import {useRecordContext} from 'components/record/useRecordContext';

const UPDATE_LIVELOX_URL = gql`
  mutation UpdateSubEventLiveloxUrl($subEventId: Int!, $url: String!) {
    updateSubEventLiveloxUrl(subEventId: $subEventId, url: $url) {
      id
      liveloxUrl
    }
  }
`;

const FORM_ID = 'update_livelox_url';

type BodyProps = {
  subEventId: number;
  initialUrl: string;
  onUpdated: () => void;
};

const UpdateLiveloxUrlBody = ({subEventId, initialUrl, onUpdated}: BodyProps) => {
  const {close} = useDialogContext();
  const [url, setUrl] = useState(initialUrl);

  useEffect(() => {
    setUrl(initialUrl);
  }, [initialUrl]);

  const [updateLiveloxUrl, {loading}] = useMutation<{updateSubEventLiveloxUrl: {id: number; liveloxUrl: string}}>(UPDATE_LIVELOX_URL);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error('Please enter a Livelox URL');
      return;
    }
    try {
      const result = await updateLiveloxUrl({variables: {subEventId, url: trimmed}});
      if (result.data?.updateSubEventLiveloxUrl) {
        toast.success('Livelox URL updated');
        onUpdated();
        close();
      }
    } catch (e) {
      if (e instanceof Error && e.message) toast.error(e.message);
    }
  };

  return (
    <form id={FORM_ID} onSubmit={handleSubmit} noValidate>
      <TextField
        autoFocus
        fullWidth
        required
        disabled={loading}
        label="Livelox class storage URL"
        placeholder="https://livelox.blob.core.windows.net/class-storage/..."
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        size="small"
        margin="normal"
        helperText="Paste the new Livelox class export JSON URL. Maps and courses are not re-imported."
      />
    </form>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const UpdateLiveloxUrlDialog = ({open, onClose}: Props) => {
  const {record: subEvent, refresh} = useRecordContext();

  return (
    <DialogContextProvider>
      <CustomDialog
        dialogTitle="Update Livelox URL"
        maxWidth="sm"
        open={open}
        resetDialog={onClose}
        primaryButton={
          <Button type="submit" form={FORM_ID} variant="contained">
            Save
          </Button>
        }
      >
        <UpdateLiveloxUrlBody subEventId={subEvent.id} initialUrl={subEvent.liveloxUrl || ''} onUpdated={() => refresh?.()} />
      </CustomDialog>
    </DialogContextProvider>
  );
};

export default UpdateLiveloxUrlDialog;
